import { FC, useEffect, useMemo } from 'react';

import { ShareClass, useLocale } from '../../../../hooks/index';
import { stakeholderTypeTranslation } from '../../../../translations/stakeholder-translation.util';
import { Column, DetailsSortVariant, StakeholderStats } from '../../../../types/snapshot.types';
import { StakeholderType } from '../../../../types/stakeholder.types';
import { filterByAllFields } from '../../../../utils/filter-by-all-fields.util';
import { toRound } from '../../../../utils/get-rounded-number.util';
import { mapCapTablePoolShareClassAmounts } from '../../../../utils/pool-share-class-columns.util';
import { calcTotal } from '../../../../utils/reduce-utils.util';
import { toNumber } from '../../../../utils/to-number.util';
import { formatDividendAmount } from '../../../dividends/utils/format-dividend-amount.util';
import {
  groupShareClassHoldersByOrdering,
  groupStakeholdersByTypeOrdered,
  sortByIssuedPercentageDesc,
  sortStakeholderTypesByOrdering,
} from '../../utils/sort-cap-table-rows.util';
import { ShareClassTableItem } from '../table-variants/items/share-class-table-item';
import { StakeholderTableItem } from '../table-variants/items/stakeholder-table-item';
import {
  DEFAULT_CAP_TABLE_ORDERING,
  getEquityPoolsGrantedDilutedShares,
  getPoolGrantedRowDilutedShares,
  getPoolShareClassAmountTotal,
  getShareClasses,
  isSearchWithoutMatches,
  SnapshotViewByProps,
} from './snapshot-view-by.types';

const EMPTY_STAKEHOLDERS: StakeholderStats[] = [];

export const SnapshotViewByAll: FC<SnapshotViewByProps> = ({
  snapshot: {
    stats: { stakeholdersTypes, stakeholders, pools, shareClasses },
    issuedShares: issuedTotal,
    dilutedShares: dilutedTotal,
  },
  defaultShareClassId: providedShareClassId,
  ordering = DEFAULT_CAP_TABLE_ORDERING,
  viewBy,
  visibleColumns,
  type,
  search,
  setEmptySearchResult,
  possibleShareClasses,
}) => {
  // Skipped entirely when the caller already knows the id — a public viewer cannot make this call.
  const { shareClass } = ShareClass.useDefaultShareClass(!providedShareClassId);

  const defaultShareClassId = providedShareClassId || shareClass?.id || '';

  const { messagesLocale } = useLocale();

  const stakeholdersSearchResult = useMemo(
    () => filterByAllFields(stakeholders, search),
    [stakeholders, search],
  );

  const shareClassesSearchResult = useMemo(
    () => filterByAllFields(shareClasses, search),
    [shareClasses, search],
  );
  const stakeholdersTypesSearchResult = useMemo(
    () => filterByAllFields(stakeholdersTypes, search),
    [stakeholdersTypes, search],
  );

  const stakeholderTypesWithDiluted = useMemo(
    () =>
      sortStakeholderTypesByOrdering(
        stakeholdersTypesSearchResult.filter((stakeholder) => stakeholder.diluted > 0),
        ordering,
        (type) => messagesLocale[stakeholderTypeTranslation[type]],
      ),
    [messagesLocale, ordering, stakeholdersTypesSearchResult],
  );

  const stakeholdersWithDiluted = useMemo(
    () => stakeholdersSearchResult.filter((stakeholder) => stakeholder.diluted > 0),
    [stakeholdersSearchResult],
  );

  const stakeholdersByType = useMemo(
    () => groupStakeholdersByTypeOrdered(stakeholdersWithDiluted, ordering),
    [ordering, stakeholdersWithDiluted],
  );

  const holdersByShareClassId = useMemo(
    () => groupShareClassHoldersByOrdering(stakeholdersWithDiluted, ordering),
    [ordering, stakeholdersWithDiluted],
  );

  const sortedShareClasses = useMemo(
    () => sortByIssuedPercentageDesc(shareClasses),
    [shareClasses],
  );

  useEffect(() => {
    const matchedRowCount =
      type === DetailsSortVariant.BY_SHARE_CLASS
        ? shareClassesSearchResult.length
        : Math.min(stakeholdersWithDiluted.length, stakeholderTypesWithDiluted.length);

    setEmptySearchResult(isSearchWithoutMatches(search, matchedRowCount));
  }, [
    search,
    setEmptySearchResult,
    shareClassesSearchResult.length,
    stakeholderTypesWithDiluted.length,
    stakeholdersWithDiluted.length,
    type,
  ]);

  const equityPoolGrantable = pools?.filter(
    (pool) =>
      !pool.isTerminated || new Date(pool?.terminationDate)?.getTime() > new Date().getTime(),
  );

  const terminatedPoolsGrantable = pools?.filter(
    (pool) =>
      pool.isTerminated && new Date(pool?.terminationDate)?.getTime() <= new Date().getTime(),
  );
  const equityPoolsGranted = [...equityPoolGrantable, ...terminatedPoolsGrantable];
  const totalEquityPlansGrantable = calcTotal(equityPoolGrantable, ({ dilluted }) =>
    toNumber(dilluted),
  );

  switch (type) {
    case DetailsSortVariant.BY_SHARE_CLASS:
      const issuedPercentageTotal = calcTotal(
        shareClasses,
        ({ issuedPercentage }) => issuedPercentage,
      );

      const dilutedPercentageTotal = calcTotal(
        shareClasses,
        ({ dilutedPercentage }) => dilutedPercentage,
      );

      const votingRightPercentageTotal = calcTotal(
        shareClasses,
        ({ votingPercentage }) => votingPercentage,
      );

      const votingRightTotal = calcTotal(shareClasses, ({ votingRight }) => votingRight);

      const investmentTotal = calcTotal(shareClasses, ({ investment }) => investment);
      const dividendsTotal = calcTotal(shareClasses, ({ dividends }) => toNumber(dividends));

      const convertedTotal = calcTotal(shareClasses, ({ converted }) => converted);
      const convertedPercentageTotal = calcTotal(
        shareClasses,
        ({ convertedPercentage }) => convertedPercentage,
      );

      const totalNoStakeholders = calcTotal(shareClasses, ({ noStakeholders }) => noStakeholders);

      const equityPoolsGrantedDilutedShares = getEquityPoolsGrantedDilutedShares({
        dilutedTotal,
        dilutedInOtherRows: calcTotal(shareClasses, ({ diluted }) => diluted),
        grantablePoolDiluted: totalEquityPlansGrantable,
      });
      const equityPoolsGrantedDilutedPercentage =
        dilutedTotal > 0 ? equityPoolsGrantedDilutedShares / dilutedTotal : 0;

      return (
        <>
          {sortedShareClasses.map(
            ({
              diluted,
              dilutedPercentage,
              issued,
              issuedPercentage,
              investment,
              dividends,
              name,
              votingPercentage,
              votingRight,
              id,
              avgSharePrice,
              noStakeholders,
              converted,
              convertedPercentage,
            }) => {
              if (!possibleShareClasses.includes(name) && id !== defaultShareClassId) return null;
              const shareClassStakeholders = holdersByShareClassId.get(id) ?? EMPTY_STAKEHOLDERS;

              return (
                <ShareClassTableItem
                  fields={{
                    [Column.SHARE_CLASS_NAME]: null,
                    [Column.STAKEHOLDER_NAME]: null,
                    [Column.TYPE]: null,
                    [Column.NAME]: name,
                    [Column.ISSUED]: toRound(issuedPercentage * 100),
                    [Column.DILUTED]: toRound(dilutedPercentage * 100),
                    [Column.VOTING]: toRound(votingPercentage * 100),
                    [Column.NO_ISSUED]: issued.toLocaleString('en-US'),
                    [Column.DILUTED_SHARES]: diluted.toLocaleString('en-US'),
                    [Column.VOTING_RIGHTS]: votingRight.toLocaleString('en-US'),
                    [Column.NO_OF_STAKEHOLDERS]: noStakeholders,
                    [Column.INVESTMENT]: investment.toLocaleString('en-US'),
                    [Column.DIVIDENDS]: formatDividendAmount(toNumber(dividends)),
                    [Column.AVERAGE_SHARE_PRICE]: toRound(avgSharePrice),
                    [Column.CONVERTED_SHARES]: converted,
                    [Column.CONVERTED_PERCENTAGE]: `${toRound(convertedPercentage * 100)}`,
                  }}
                  id={id}
                  key={id}
                  ordering={ordering}
                  search={search}
                  stakeholders={shareClassStakeholders}
                  viewBy={viewBy}
                  visibleColumns={visibleColumns}
                />
              );
            },
          )}

          <ShareClassTableItem
            fields={{
              [Column.STAKEHOLDER_NAME]: null,
              [Column.TYPE]: null,
              [Column.SHARE_CLASS_NAME]: null,
              [Column.NO_OF_STAKEHOLDERS]: '-',
              [Column.NAME]: messagesLocale['EQUITY_POOLS_GRANTABLE'],
              [Column.ISSUED]: toRound(
                calcTotal(equityPoolGrantable, ({ issuedPercentage }) => issuedPercentage) * 100,
              ),
              [Column.DILUTED]: toRound(
                calcTotal(equityPoolGrantable, ({ dillutedPercentage }) => dillutedPercentage) *
                  100,
              ),
              [Column.NO_ISSUED]: calcTotal(equityPoolGrantable, ({ issued }) => issued),
              [Column.VOTING]: 0,
              [Column.DILUTED_SHARES]: totalEquityPlansGrantable,
              [Column.INVESTMENT]: 0,
              [Column.DIVIDENDS]: 0,
              [Column.AVERAGE_SHARE_PRICE]: 0,
              [Column.VOTING_RIGHTS]: 0,
              [Column.CONVERTED_SHARES]: calcTotal(
                equityPoolGrantable,
                ({ converted }) => converted,
              ),
              [Column.CONVERTED_PERCENTAGE]: toRound(
                calcTotal(equityPoolGrantable, ({ convertedPercentage }) => convertedPercentage) *
                  100,
              ),
            }}
            isEquityPoolsGrantable={true}
            stakeholders={equityPoolGrantable?.map(
              ({
                dilluted,
                id,
                name,
                dillutedPercentage,
                issued,
                issuedPercentage,
                converted,
                convertedPercentage,
              }) => ({
                id,
                name,
                diluted: dilluted,
                dilutedPercentage: dillutedPercentage * 100,
                invested: 0,
                dividends: 0,
                issued,
                issuedPercentage: issuedPercentage * 100,
                shareClasses: [],
                type: StakeholderType.VCS,
                votingPercentage: 0,
                votingRight: 0,
                avgSharePrice: 0,
                converted,
                convertedPercentage,
              }),
            )}
            viewBy={viewBy}
            visibleColumns={visibleColumns}
          />
          <ShareClassTableItem
            fields={{
              [Column.STAKEHOLDER_NAME]: null,
              [Column.TYPE]: null,
              [Column.SHARE_CLASS_NAME]: null,
              [Column.NO_OF_STAKEHOLDERS]: '-',
              [Column.NAME]: 'Equity Pools Granted',
              [Column.ISSUED]: toRound(
                calcTotal(
                  [...equityPoolGrantable, ...terminatedPoolsGrantable],
                  ({ unvestedPercentage }) => unvestedPercentage,
                ) * 100,
              ),
              [Column.DILUTED]: toRound(equityPoolsGrantedDilutedPercentage * 100),
              [Column.NO_ISSUED]: calcTotal(equityPoolsGranted, ({ unvested }) => unvested),
              [Column.VOTING]: 0,
              [Column.DILUTED_SHARES]: equityPoolsGrantedDilutedShares,
              [Column.INVESTMENT]: 0,
              [Column.DIVIDENDS]: 0,
              [Column.AVERAGE_SHARE_PRICE]: 0,
              [Column.VOTING_RIGHTS]: 0,
              [Column.CONVERTED_SHARES]: 0,
              [Column.CONVERTED_PERCENTAGE]: 0,
            }}
            isEquityPoolsGrantable={true}
            stakeholders={equityPoolsGranted?.map((pool) => {
              const poolGrantedDiluted = getPoolGrantedRowDilutedShares(
                pool,
                equityPoolsGrantedDilutedShares,
                equityPoolGrantable,
              );

              return {
                id: pool.id,
                name: pool.name,
                diluted: poolGrantedDiluted,
                dilutedPercentage: dilutedTotal > 0 ? poolGrantedDiluted / dilutedTotal : 0,
                invested: 0,
                dividends: 0,
                issued: pool.unvested,
                issuedPercentage: pool.unvestedPercentage * 100,
                shareClasses: [],
                type: StakeholderType.VCS,
                votingPercentage: 0,
                votingRight: 0,
                avgSharePrice: 0,
                converted: 0,
                convertedPercentage: 0,
              };
            })}
            viewBy={viewBy}
            visibleColumns={visibleColumns}
          />
          <ShareClassTableItem
            className="font-[550] text-gray-800"
            fields={{
              [Column.NAME]: messagesLocale['TOTAL'],
              [Column.STAKEHOLDER_NAME]: null,
              [Column.TYPE]: null,
              [Column.SHARE_CLASS_NAME]: null,
              [Column.NO_OF_STAKEHOLDERS]: totalNoStakeholders,
              [Column.ISSUED]: toRound(
                (issuedPercentageTotal +
                  calcTotal(
                    equityPoolGrantable,
                    ({ issuedPercentage, unvestedPercentage }) =>
                      issuedPercentage + unvestedPercentage,
                  ) +
                  calcTotal(
                    terminatedPoolsGrantable,
                    ({ unvestedPercentage }) => unvestedPercentage,
                  )) *
                  100,
              ),
              [Column.DILUTED]: toRound(
                (dilutedPercentageTotal +
                  calcTotal(equityPoolGrantable, ({ dillutedPercentage }) => dillutedPercentage) +
                  equityPoolsGrantedDilutedPercentage) *
                  100,
              ),
              [Column.VOTING]: toRound(votingRightPercentageTotal * 100),
              [Column.NO_ISSUED]: issuedTotal.toLocaleString('en-US'),
              [Column.DILUTED_SHARES]: dilutedTotal.toLocaleString('en-US'),
              [Column.VOTING_RIGHTS]: votingRightTotal.toLocaleString('en-US'),
              [Column.INVESTMENT]: investmentTotal.toLocaleString('en-US'),
              [Column.DIVIDENDS]: formatDividendAmount(dividendsTotal),
              [Column.AVERAGE_SHARE_PRICE]: '-',
              [Column.CONVERTED_SHARES]:
                convertedTotal + calcTotal(equityPoolGrantable, ({ converted }) => converted),
              [Column.CONVERTED_PERCENTAGE]: `${toRound((convertedPercentageTotal + calcTotal(equityPoolGrantable, ({ convertedPercentage }) => convertedPercentage)) * 100)}`,
            }}
            viewBy={viewBy}
            visibleColumns={visibleColumns}
          />
        </>
      );
    case DetailsSortVariant.BY_STAKEHOLDER: {
      const { otherShareClasses, defaultShareClass } = getShareClasses(
        defaultShareClassId,
        shareClasses,
      );

      const issuedPercentageTotal = calcTotal(
        stakeholderTypesWithDiluted,
        ({ issuedPercentage }) => issuedPercentage,
      );

      const dilutedPercentageTotal = calcTotal(
        stakeholderTypesWithDiluted,
        ({ dilutedPercentage }) => dilutedPercentage,
      );

      const votingRightPercentageTotal = calcTotal(
        stakeholderTypesWithDiluted,
        ({ votingPercentage }) => votingPercentage,
      );

      const votingRightTotal = calcTotal(
        stakeholderTypesWithDiluted,
        ({ votingRight }) => votingRight,
      );

      const convertedTotal = calcTotal(stakeholderTypesWithDiluted, ({ converted }) => converted);
      const convertedPercentageTotal = calcTotal(
        stakeholderTypesWithDiluted,
        ({ convertedPercentage }) => convertedPercentage,
      );

      const commonStockTotal =
        toNumber(defaultShareClass?.issued) +
        calcTotal(
          equityPoolGrantable.filter(({ shareClassId }) => shareClassId === defaultShareClassId),
          ({ issued, unvested }) => issued + unvested,
        ) +
        calcTotal(
          terminatedPoolsGrantable.filter(
            ({ shareClassId }) => shareClassId === defaultShareClassId,
          ),
          ({ unvested }) => unvested,
        );

      const shareClassesTotal = otherShareClasses
        .filter(({ name }) => possibleShareClasses.includes(name))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ issued, id }) => {
          const issuedAmount =
            issued + getPoolShareClassAmountTotal(equityPoolGrantable, id, 'issued');
          const unvestedAmount =
            getPoolShareClassAmountTotal(equityPoolGrantable, id, 'unvested') +
            getPoolShareClassAmountTotal(terminatedPoolsGrantable, id, 'unvested');
          return issuedAmount + unvestedAmount;
        });

      const investmentTotal = calcTotal(stakeholdersWithDiluted, ({ invested }) => invested);
      const dividendsTotal = calcTotal(stakeholdersWithDiluted, ({ dividends }) =>
        toNumber(dividends),
      );

      const equityPoolsGrantedDilutedShares = getEquityPoolsGrantedDilutedShares({
        dilutedTotal,
        dilutedInOtherRows: calcTotal(stakeholderTypesWithDiluted, ({ diluted }) => diluted),
        grantablePoolDiluted: totalEquityPlansGrantable,
      });
      const equityPoolsGrantedDilutedPercentage =
        dilutedTotal > 0 ? equityPoolsGrantedDilutedShares / dilutedTotal : 0;

      return (
        <>
          {stakeholderTypesWithDiluted.map(
            (
              {
                avgSharePrice,
                diluted,
                dilutedPercentage,
                issued,
                issuedPercentage,
                type,
                votingPercentage,
                shareClasses,
                votingRight,
                converted,
                convertedPercentage,
              },
              i,
            ) => {
              const stakeholders = stakeholdersByType.get(type) ?? EMPTY_STAKEHOLDERS;
              const invested = calcTotal(stakeholders, ({ invested }) => invested);
              const dividends = calcTotal(stakeholders, ({ dividends }) => toNumber(dividends));

              const { defaultShareClass, otherShareClasses } = getShareClasses(
                defaultShareClassId,
                shareClasses,
              );

              const otherShareClassesMap = otherShareClasses
                .sort((a, b) => a.name.localeCompare(b.name))
                .reduce<Record<string, (typeof otherShareClasses)[0]>>((acc, curr) => {
                  acc[curr.name] = curr;
                  return acc;
                }, {});

              const stakeholderWithCurrentType = stakeholders;

              return (
                <StakeholderTableItem
                  allShareClassNames={possibleShareClasses}
                  defaultShareClassId={defaultShareClassId}
                  fields={{
                    [Column.NAME]: messagesLocale[stakeholderTypeTranslation[type]],
                    [Column.STAKEHOLDER_NAME]: null,
                    [Column.TYPE]: null,
                    [Column.ISSUED]: toRound(issuedPercentage * 100),
                    [Column.DILUTED]: toRound(dilutedPercentage * 100),
                    [Column.VOTING]: toRound(votingPercentage * 100),
                    [Column.NO_ISSUED]: issued,
                    [Column.DILUTED_SHARES]: diluted,
                    [Column.COMMON_STOCK]: toNumber(defaultShareClass?.issued),
                    [Column.OTHER_SHARE_CLASSES]: possibleShareClasses.map((name) => {
                      return otherShareClassesMap[name] ? otherShareClassesMap[name].issued : 0;
                    }),
                    [Column.VOTING_RIGHTS]: votingRight.toLocaleString('en-US'),
                    [Column.INVESTMENT]: invested.toLocaleString('en-US'),
                    [Column.DIVIDENDS]: formatDividendAmount(toNumber(dividends)),
                    [Column.AVERAGE_SHARE_PRICE]: toRound(avgSharePrice),
                    [Column.CONVERTED_SHARES]: converted,
                    [Column.CONVERTED_PERCENTAGE]: toRound(convertedPercentage * 100),
                  }}
                  key={`${i}_${issued}${type}`}
                  ordering={ordering}
                  search={search}
                  stakeholders={stakeholderWithCurrentType}
                  viewBy={viewBy}
                  visibleColumns={visibleColumns}
                />
              );
            },
          )}
          <StakeholderTableItem
            allShareClassNames={possibleShareClasses}
            defaultShareClassId={defaultShareClassId}
            fields={{
              [Column.NAME]: messagesLocale['EQUITY_POOLS_GRANTABLE'],
              [Column.STAKEHOLDER_NAME]: 0,
              [Column.TYPE]: 0,
              [Column.ISSUED]: toRound(
                calcTotal(equityPoolGrantable, ({ issuedPercentage }) => issuedPercentage) * 100,
              ),
              [Column.DILUTED]: toRound(
                calcTotal(equityPoolGrantable, ({ dillutedPercentage }) => dillutedPercentage) *
                  100,
              ),
              [Column.NO_ISSUED]: calcTotal(equityPoolGrantable, ({ issued }) => issued),
              [Column.VOTING]: 0,
              [Column.DILUTED_SHARES]: totalEquityPlansGrantable,
              [Column.COMMON_STOCK]: calcTotal(
                equityPoolGrantable.filter(
                  ({ shareClassId }) => shareClassId === defaultShareClassId,
                ),
                ({ issued }) => issued,
              ),
              [Column.OTHER_SHARE_CLASSES]: possibleShareClasses.map((name) => {
                const shareClass = otherShareClasses.find((shareClass) => shareClass.name === name);

                return shareClass
                  ? getPoolShareClassAmountTotal(equityPoolGrantable, shareClass.id, 'issued')
                  : 0;
              }),
              [Column.INVESTMENT]: 0,
              [Column.DIVIDENDS]: 0,
              [Column.AVERAGE_SHARE_PRICE]: 0,
              [Column.VOTING_RIGHTS]: 0,
              [Column.CONVERTED_SHARES]: calcTotal(
                equityPoolGrantable,
                ({ converted }) => converted,
              ),
              [Column.CONVERTED_PERCENTAGE]: toRound(
                calcTotal(equityPoolGrantable, ({ convertedPercentage }) => convertedPercentage) *
                  100,
              ),
            }}
            search={search}
            stakeholders={equityPoolGrantable.map(
              ({
                dilluted,
                id,
                name,
                dillutedPercentage,
                issued,
                issuedPercentage,
                converted,
                convertedPercentage,
                shareClassId,
              }) => ({
                id: id,
                name: name,
                diluted: dilluted,
                dilutedPercentage: dillutedPercentage,
                invested: 0,
                dividends: 0,
                issued,
                issuedPercentage,
                shareClasses: mapCapTablePoolShareClassAmounts(shareClasses, shareClassId, issued),
                type: StakeholderType.VCS,
                votingPercentage: 0,
                votingRight: 0,
                avgSharePrice: 0,
                converted,
                convertedPercentage,
              }),
            )}
            viewBy={viewBy}
            visibleColumns={visibleColumns}
          />
          <StakeholderTableItem
            allShareClassNames={possibleShareClasses}
            defaultShareClassId={defaultShareClassId}
            fields={{
              [Column.NAME]: 'Equity Pools Granted',
              [Column.STAKEHOLDER_NAME]: 0,
              [Column.TYPE]: 0,
              [Column.ISSUED]: toRound(
                calcTotal(
                  [...equityPoolGrantable, ...terminatedPoolsGrantable],
                  ({ unvestedPercentage }) => unvestedPercentage,
                ) * 100,
              ),
              [Column.DILUTED]: toRound(equityPoolsGrantedDilutedPercentage * 100),
              [Column.NO_ISSUED]: calcTotal(equityPoolsGranted, ({ unvested }) => unvested),
              [Column.VOTING]: 0,
              [Column.DILUTED_SHARES]: equityPoolsGrantedDilutedShares,
              [Column.COMMON_STOCK]: calcTotal(
                [...equityPoolGrantable, ...terminatedPoolsGrantable].filter(
                  ({ shareClassId }) => shareClassId === defaultShareClassId,
                ),
                ({ unvested }) => unvested,
              ),
              [Column.OTHER_SHARE_CLASSES]: possibleShareClasses.map((name) => {
                const shareClass = otherShareClasses.find((shareClass) => shareClass.name === name);

                return shareClass
                  ? getPoolShareClassAmountTotal(equityPoolsGranted, shareClass.id, 'unvested')
                  : 0;
              }),
              [Column.INVESTMENT]: 0,
              [Column.DIVIDENDS]: 0,
              [Column.AVERAGE_SHARE_PRICE]: 0,
              [Column.VOTING_RIGHTS]: 0,
              [Column.CONVERTED_SHARES]: 0,
              [Column.CONVERTED_PERCENTAGE]: 0,
            }}
            search={search}
            stakeholders={equityPoolsGranted.map((pool) => {
              const poolGrantedDiluted = getPoolGrantedRowDilutedShares(
                pool,
                equityPoolsGrantedDilutedShares,
                equityPoolGrantable,
              );

              return {
                id: pool.id,
                name: pool.name,
                diluted: poolGrantedDiluted,
                dilutedPercentage: dilutedTotal > 0 ? poolGrantedDiluted / dilutedTotal : 0,
                invested: 0,
                dividends: 0,
                issued: pool.unvested,
                issuedPercentage: pool.unvestedPercentage,
                shareClasses: mapCapTablePoolShareClassAmounts(
                  shareClasses,
                  pool.shareClassId,
                  pool.unvested,
                ),
                type: StakeholderType.VCS,
                votingPercentage: 0,
                votingRight: 0,
                avgSharePrice: 0,
                converted: 0,
                convertedPercentage: 0,
              };
            })}
            viewBy={viewBy}
            visibleColumns={visibleColumns}
          />
          <StakeholderTableItem
            allShareClassNames={possibleShareClasses}
            className="font-[550] text-gray-800"
            defaultShareClassId={defaultShareClassId}
            fields={{
              [Column.NAME]: messagesLocale['TOTAL'],
              [Column.STAKEHOLDER_NAME]: null,
              [Column.TYPE]: null,
              [Column.ISSUED]: toRound(
                (issuedPercentageTotal +
                  calcTotal(
                    equityPoolGrantable,
                    ({ issuedPercentage, unvestedPercentage }) =>
                      issuedPercentage + unvestedPercentage,
                  ) +
                  calcTotal(
                    terminatedPoolsGrantable,
                    ({ unvestedPercentage }) => unvestedPercentage,
                  )) *
                  100,
              ),
              [Column.DILUTED]: toRound(
                (dilutedPercentageTotal +
                  calcTotal(equityPoolGrantable, ({ dillutedPercentage }) => dillutedPercentage) +
                  equityPoolsGrantedDilutedPercentage) *
                  100,
              ),
              [Column.VOTING]: toRound(votingRightPercentageTotal * 100),
              [Column.NO_ISSUED]: issuedTotal.toLocaleString('en-US'),
              [Column.DILUTED_SHARES]: dilutedTotal.toLocaleString('en-US'),
              [Column.COMMON_STOCK]: commonStockTotal.toLocaleString('en-US'),
              [Column.OTHER_SHARE_CLASSES]: shareClassesTotal,
              [Column.VOTING_RIGHTS]: votingRightTotal.toLocaleString('en-US'),
              [Column.INVESTMENT]: investmentTotal.toLocaleString('en-US'),
              [Column.DIVIDENDS]: formatDividendAmount(dividendsTotal),
              [Column.AVERAGE_SHARE_PRICE]: '-',
              [Column.CONVERTED_SHARES]:
                convertedTotal + calcTotal(equityPoolGrantable, ({ converted }) => converted),
              [Column.CONVERTED_PERCENTAGE]: toRound(
                (convertedPercentageTotal +
                  calcTotal(
                    equityPoolGrantable,
                    ({ convertedPercentage }) => convertedPercentage,
                  )) *
                  100,
              ),
            }}
            viewBy={viewBy}
            visibleColumns={visibleColumns}
          />
        </>
      );
    }

    default:
      return <></>;
  }
};
